import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { formatDateTime as fmtDT } from '../lib/dateFormat';
import {
  Ship, Filter, RefreshCw, Loader2, X, Upload, FileText, ChevronRight, ChevronDown,
  AlertTriangle, CheckCircle2, Search, Download, ExternalLink, Lock, Globe2,
  Trash2, Shield, Edit3, Save, MessageSquarePlus, Clock, Pencil, Share2, Copy,
  SlidersHorizontal, Package2, Settings, Eye, EyeOff,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import CustomerNameLabel from '../components/CustomerNameLabel';
import NewExportOrderModal from '../components/NewExportOrderModal';
import ExportMaintenanceSection from '../components/ExportMaintenanceSection';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import Rial from '../components/Rial';

// ============================================================
// AttachmentLightbox — Feb 2026
// Full-screen preview for timeline-attached documents. Images
// render via <img>, PDFs / Office docs load Google Drive's
// `/file/d/{id}/preview` viewer inside an <iframe>. Escape key +
// backdrop click both close. Kept file-local so both admin and
// (future) share-view surfaces can drop it in unchanged.
// ============================================================
const AttachmentLightbox = ({ open, attachment, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !attachment) return null;
  const fid = attachment.file_id;
  const name = attachment.name || 'Document';
  const mime = (attachment.mime || '').toLowerCase();
  const isImage = mime.startsWith('image/');
  const src = isImage
    ? `https://drive.google.com/thumbnail?id=${encodeURIComponent(fid)}&sz=w1600`
    : `https://drive.google.com/file/d/${encodeURIComponent(fid)}/preview`;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8"
      style={{ background: 'rgba(15,23,42,0.88)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="admin-export-attachment-lightbox"
    >
      <div className="relative w-full max-w-[1080px] h-[84vh] rounded-2xl overflow-hidden shadow-2xl bg-[#0F172A]">
        <div className="absolute left-3 top-3 right-14 text-white text-xs font-semibold opacity-85 truncate">
          {name}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 text-white text-xl leading-none"
          aria-label="Close preview"
          data-testid="admin-export-lightbox-close"
        >
          ×
        </button>
        {isImage ? (
          <img src={src} alt={name} className="w-full h-full object-contain bg-[#0F172A]" />
        ) : (
          <iframe
            title={name}
            src={src}
            className="w-full h-full border-0 bg-[#0F172A]"
            allow="autoplay"
            allowFullScreen
          />
        )}
      </div>
    </div>
  );
};

// ============================================================
// AdminExportDashboard — Feb 2026
// Cross-cutting Export Order operations page. Each order carries a
// `department_status` object with 5 stages; each stage has its own
// status pill + notes + document uploads. Only the user's own dept
// row is editable (unless super admin). When ALL five hit Completed
// the order auto-flips to `shipped` and the whole detail panel
// becomes read-only.
// ============================================================
const DEPT_KEYS = ['documentation', 'transport', 'accounts', 'logistics'];
// Legacy label retained so historical timeline entries tagged with the
// retired `clearing` department still render with a sensible chip.
// `DEPT_KEYS` above is the authoritative list of ACTIVE departments —
// new posts, dropdowns and workflow chips no longer expose Clearing.
const DEPT_LABEL = {
  documentation: 'Documentation',
  clearing: 'Clearing',
  transport: 'Transport',
  accounts: 'Accounts',
  logistics: 'Logistics',
};
const STAGE_STATUSES = ['Pending', 'In Progress', 'Submitted', 'Approved', 'Completed'];
const STATUS_TONE = {
  Pending: 'bg-neutral-100 text-neutral-600 border-neutral-200',
  'In Progress': 'bg-amber-100 text-amber-800 border-amber-200',
  Submitted: 'bg-blue-100 text-blue-800 border-blue-200',
  Approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Completed: 'bg-emerald-700 text-white border-emerald-700',
};

const AdminExportDashboard = () => {
  const { authHeaders, isSuperAdmin } = useAdminAuth();
  const { toast } = useToast();

  // Feb 2026 — Super-admin overrides for the Export Dashboard.
  // Loaded from the site-settings-backed
  // `/admin/export-dashboard/config` endpoint so hidden departments,
  // renamed dept labels, and renamed status labels all render
  // consistently on this dashboard, its detail sheet, and the
  // public timeline. Super admin always sees the RAW keys/values
  // (`documentation`, `In Progress`, etc.) — the display transform
  // only applies to non-super-admin users and public viewers.
  const [exportConfig, setExportConfig] = useState({
    hidden_departments: [],
    department_labels: {},
    status_labels: {},
    department_options: {},
  });
  const displayDeptLabel = React.useCallback((key) => {
    if (isSuperAdmin) return DEPT_LABEL[key] || key;
    return (exportConfig.department_labels || {})[key] || DEPT_LABEL[key] || key;
  }, [exportConfig, isSuperAdmin]);
  const displayStatusLabel = React.useCallback((status) => {
    if (isSuperAdmin) return status;
    return (exportConfig.status_labels || {})[status] || status;
  }, [exportConfig, isSuperAdmin]);
  const isDeptHidden = React.useCallback((key) => {
    if (isSuperAdmin) return false;
    return (exportConfig.hidden_departments || []).includes(key);
  }, [exportConfig, isSuperAdmin]);
  const visibleDeptKeys = React.useMemo(
    () => DEPT_KEYS.filter((k) => !isDeptHidden(k)),
    [isDeptHidden],
  );

  // Feb 2026 — "shipped" here means any terminal-complete order
  // status: shipped / delivered / received / completed. Kept in one
  // place so the tab bar, detail read-only banner, and row badges all
  // agree.
  const SHIPPED_STATUSES = ['shipped', 'delivered', 'received', 'completed'];
  const isShippedStatus = (s) => SHIPPED_STATUSES.includes(String(s || '').toLowerCase());

  // Tabs — Active vs. Shipped (see PRD).
  const [tab, setTab] = useState('active');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, shipped: 0, in_progress: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterOrder, setFilterOrder] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterDeptStatus, setFilterDeptStatus] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Detail panel
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Feb 2026 — collapsible per-row detail accordion + column config
  // dialog (super admin only). Config is a single global doc so the
  // whole team + the public timeline all see the same layout.
  const [expandedRows, setExpandedRows] = useState({});
  const [columnPrefs, setColumnPrefs] = useState({ enabled_fields: [] });
  const [availableFields, setAvailableFields] = useState([]);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [columnsDraft, setColumnsDraft] = useState([]);
  const [columnsSaving, setColumnsSaving] = useState(false);
  // Feb 2026 — Super-admin Department & Status configuration dialog.
  const [deptConfigOpen, setDeptConfigOpen] = useState(false);
  const toggleRowExpand = (id) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const fetchColumnPrefs = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/export-timeline/column-prefs`, { headers: authHeaders });
      setColumnPrefs(data?.config || { enabled_fields: [] });
      setAvailableFields(data?.available_fields || []);
    } catch (_) {
      // Silently degrade — an admin without the endpoint still sees the tab, just no accordion.
    }
  };

  const openColumnsDialog = () => {
    setColumnsDraft([...(columnPrefs.enabled_fields || [])]);
    setColumnsDialogOpen(true);
  };
  const saveColumnsDialog = async () => {
    setColumnsSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/export-timeline/column-prefs`,
        { enabled_fields: columnsDraft },
        { headers: authHeaders },
      );
      setColumnPrefs(data?.config || { enabled_fields: columnsDraft });
      setColumnsDialogOpen(false);
      toast({ title: 'Columns saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setColumnsSaving(false);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (tab === 'shipped') {
        params.order_status = 'shipped';
      } else {
        params.include_shipped = false;
      }
      if (filterCountry) params.country = filterCountry;
      if (filterDeptStatus && filterDeptStatus !== 'all') params.dept_status = filterDeptStatus;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const { data } = await axios.get(`${API}/admin/export-orders`, { headers: authHeaders, params });
      let list = Array.isArray(data?.rows) ? data.rows : [];
      if (filterOrder.trim()) {
        const q = filterOrder.trim().toLowerCase();
        list = list.filter((r) => (r.order_number || '').toLowerCase().includes(q));
      }
      setRows(list);
      setSummary(data?.summary || { total: 0, shipped: 0, in_progress: 0, pending: 0 });
      // Feb 2026 — refresh dashboard config from the same response so
      // renamed dept/status labels and hidden depts propagate on the
      // next poll without a second round-trip.
      if (data?.export_dashboard_config) {
        setExportConfig({
          hidden_departments: data.export_dashboard_config.hidden_departments || [],
          department_labels: data.export_dashboard_config.department_labels || {},
          status_labels: data.export_dashboard_config.status_labels || {},
          department_options: data.export_dashboard_config.department_options || {},
        });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load export orders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [tab]);

  // Load column config once on mount (silently no-ops if the endpoint
  // isn't available or the user lacks permission).
  useEffect(() => { fetchColumnPrefs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const fetchDetail = async (orderId) => {
    setSelectedId(orderId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/export-orders/${orderId}`, { headers: authHeaders });
      setDetail(data);
    } catch (err) {
      toast({ title: 'Failed to load order', description: err?.response?.data?.detail || err?.message });
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Feb 2026 — Super admin permanent delete. Confirms twice, releases
  // any placement allocations locked by this order, then hard-deletes
  // the doc (with every embedded timeline / stage / update / plant
  // notification). Refreshes the list on success.
  const deleteOrder = async (row) => {
    if (!row?.id) return;
    const label = row.order_number || row.id;
    if (!window.confirm(
      `Permanently delete export order ${label}?\n\n`
      + 'This removes the order document plus every associated stage, '
      + 'timeline entry, and plant notification. Any locked stock will '
      + 'be released. This cannot be undone.'
    )) return;
    try {
      const { data } = await axios.delete(
        `${API}/admin/export-orders/${row.id}`,
        { headers: authHeaders },
      );
      toast({
        title: 'Export order deleted',
        description: `${label} removed${data?.released_placements ? ` · ${data.released_placements} placement(s) released` : ''}.`,
      });
      // Close detail sheet if it's showing the row we just deleted.
      if (selectedId === row.id) {
        setSelectedId(null);
        setDetail(null);
      }
      fetchList();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err?.message });
    }
  };

  const clearFilters = () => {
    setFilterOrder('');
    setFilterCountry('');    setFilterDeptStatus('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setTimeout(fetchList, 0);
  };

  const hasAnyFilter = filterOrder || filterCountry || (filterDeptStatus && filterDeptStatus !== 'all') || filterDateFrom || filterDateTo;

  // ---- Share links (super admin — mint gated public timeline URLs) ----
  const [shareOpen, setShareOpen] = useState(false);

  // Aug 2026 — Inline "New Export Order" flow. Any admin holding
  // `orders.create`, the bare `orders` module, wildcard, or the new
  // `orders.export_only` permission (plus super admins) can cut an
  // export order right from this page. Products are lazily fetched
  // on first click of the button to avoid slowing the dashboard's
  // initial paint.
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [orderProducts, setOrderProducts] = useState([]);
  const [orderProductsLoaded, setOrderProductsLoaded] = useState(false);
  const [canCreateExportOrder, setCanCreateExportOrder] = useState(false);

  useEffect(() => {
    // Resolve create-permission from `/admin/me`. Super admin flag is
    // already surfaced by the context; sub-admins need the explicit
    // permission check because `useAdminAuth` doesn't expose the raw
    // permissions list.
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/me`, { headers: authHeaders });
        if (!alive) return;
        const perms = Array.isArray(data?.permissions) ? data.permissions : [];
        const allowed = !!data?.is_super_admin
          || perms.includes('*')
          || perms.includes('orders')
          || perms.includes('orders.create')
          || perms.includes('orders.export_only');
        setCanCreateExportOrder(allowed);
      } catch (_) { /* leave default false */ }
    })();
    return () => { alive = false; };
  }, [authHeaders]);

  const openNewOrder = async () => {
    if (!orderProductsLoaded) {
      try {
        const { data } = await axios.get(`${API}/admin/products`, { headers: authHeaders });
        setOrderProducts(Array.isArray(data) ? data : (data?.products || []));
        setOrderProductsLoaded(true);
      } catch (err) {
        toast({
          title: 'Could not load products',
          description: err?.response?.data?.detail || err?.message,
        });
      }
    }
    setNewOrderOpen(true);
  };

  return (
    <AdminLayout>
      <div className="max-w-[1600px] mx-auto px-4 py-6" data-testid="admin-export-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Ship className="w-6 h-6 text-[#0078D4]" />
              <h1 className="text-3xl font-bold text-[#0F172A]">Export Orders</h1>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              Department workflow — Documentation → Transport → Accounts → Logistics.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canCreateExportOrder && (
              <Button
                onClick={openNewOrder}
                className="rounded-full h-9 bg-[#0078D4] hover:bg-[#005A9E] text-white"
                data-testid="export-new-order-btn"
              >
                <Ship className="w-4 h-4 mr-2" />
                New Export Order
              </Button>
            )}
            {isSuperAdmin && (
              <Button
                onClick={openColumnsDialog}
                variant="outline"
                className="rounded-full h-9"
                data-testid="export-columns-btn"
                title="Configure which fields appear in the per-row detail dropdown"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Columns
              </Button>
            )}
            {isSuperAdmin && (
              <Button
                onClick={() => setDeptConfigOpen(true)}
                variant="outline"
                className="rounded-full h-9"
                data-testid="export-dept-config-btn"
                title="Rename departments/statuses or hide stages from non-super-admin + public views"
              >
                <Settings className="w-4 h-4 mr-2" />
                Departments
              </Button>
            )}
            <Button
              onClick={() => setShareOpen(true)}
              variant="outline"
              className="rounded-full h-9"
              data-testid="export-share-open"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Timeline
            </Button>
            <Button
              onClick={fetchList}
              disabled={loading}
              variant="outline"
              className="rounded-full h-9"
              data-testid="export-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Feb 2026 — Super-admin Maintenance section (toggle,
            notification history, tools). Gated on isSuperAdmin so
            regular admins never see it. */}
        {isSuperAdmin && (
          <ExportMaintenanceSection
            API={API}
            authHeaders={authHeaders}
            toast={toast}
          />
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4">
          {[
            { id: 'active', label: 'Active', count: summary.total },
            { id: 'shipped', label: 'Shipped', count: summary.shipped },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`export-tab-${t.id}`}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                tab === t.id
                  ? 'bg-[#0F172A] text-white'
                  : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              {t.label}{typeof t.count === 'number' ? ` (${t.count})` : ''}
            </button>
          ))}
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="export-summary">
          <SummaryTile label="Total" value={summary.total} testid="export-summary-total" />
          <SummaryTile label="In Progress" value={summary.in_progress} tone="amber" testid="export-summary-in-progress" />
          <SummaryTile label="Pending" value={summary.pending} tone="neutral" testid="export-summary-pending" />
          <SummaryTile label="Shipped" value={summary.shipped} tone="emerald" testid="export-summary-shipped" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-neutral-200 p-3 mb-4" data-testid="export-filters">
          <div className="flex items-center gap-2 mb-2 text-xs text-neutral-500 uppercase font-bold tracking-wider">
            <Filter className="w-3.5 h-3.5" /> Filters
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-1">Order #</label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={filterOrder}
                  onChange={(e) => setFilterOrder(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchList(); }}
                  placeholder="MR-00000..."
                  className="pl-8"
                  data-testid="export-filter-order"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-1">Country</label>
              <div className="relative">
                <Globe2 className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={filterCountry}
                  onChange={(e) => setFilterCountry(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchList(); }}
                  placeholder="AE, KW, Kuwait..."
                  className="pl-8"
                  data-testid="export-filter-country"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-1">Dept Stage Status</label>
              <Select value={filterDeptStatus} onValueChange={setFilterDeptStatus}>
                <SelectTrigger data-testid="export-filter-dept-status">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any status</SelectItem>
                  {STAGE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-1">From</label>
              <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} data-testid="export-filter-from" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-1">To</label>
              <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} data-testid="export-filter-to" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            {hasAnyFilter && (
              <Button onClick={clearFilters} variant="ghost" className="rounded-full h-8 text-xs" data-testid="export-clear-filters">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
            <Button onClick={fetchList} disabled={loading} className="rounded-full h-8 text-xs bg-[#0078D4] hover:bg-[#106EBE] text-white" data-testid="export-apply-filters">
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Filter className="w-3.5 h-3.5 mr-1" />} Apply
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-50 border-b border-red-200 flex items-center gap-2" data-testid="export-error">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="export-table">
              <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Order #</th>
                  <th className="text-left px-3 py-2">Business Name</th>
                  <th className="text-left px-3 py-2">Country</th>
                  <th className="text-right px-3 py-2">Total</th>
                  {visibleDeptKeys.map((d) => (
                    <th key={d} className="text-center px-3 py-2" title={displayDeptLabel(d)}>
                      {displayDeptLabel(d)}
                    </th>
                  ))}
                  <th className="text-left px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-6 text-neutral-500" data-testid="export-loading">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-8 text-neutral-500" data-testid="export-empty">
                    No export orders match the current filters.
                  </td></tr>
                )}
                {rows.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr
                      className={`border-t border-neutral-100 hover:bg-sky-50/40 ${r.status === 'shipped' ? 'bg-emerald-50/40' : ''}`}
                      data-testid={`export-row-${r.order_number}`}
                    >
                      <td className="px-3 py-2 font-mono text-[#0F172A] cursor-pointer" onClick={() => fetchDetail(r.id)}>
                        {r.order_number}
                        {r.status === 'shipped' && (
                          <span className="ml-1.5 inline-flex items-center rounded-full bg-emerald-700 text-white px-1.5 py-0.5 text-[9px] font-bold tracking-wider">SHIPPED</span>
                        )}
                      </td>
                      <td className="px-3 py-2 cursor-pointer" onClick={() => fetchDetail(r.id)}>
                        <CustomerNameLabel
                          customer={{ business_name: r.business_name, name: r.contact_name }}
                          primaryClassName="text-sm"
                          secondaryClassName="text-[10px]"
                        />
                      </td>
                      <td className="px-3 py-2 text-neutral-700 cursor-pointer" onClick={() => fetchDetail(r.id)}>
                        <span className="inline-flex items-center gap-1">
                          {r.country_code && <span className="font-mono text-[10px] bg-neutral-100 rounded px-1 py-0.5">{r.country_code}</span>}
                          {r.country_name || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold cursor-pointer" onClick={() => fetchDetail(r.id)}>
                        <Rial /> {(r.total || 0).toFixed(3)}
                      </td>
                      {visibleDeptKeys.map((d) => {
                        const stage = (r.department_status || {})[d] || {};
                        const rawStatus = stage.status || 'Pending';
                        const displayedStatus = displayStatusLabel(rawStatus);
                        return (
                          <td key={d} className="px-2 py-2 text-center cursor-pointer" onClick={() => fetchDetail(r.id)}>
                            <span
                              className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold border ${STATUS_TONE[rawStatus] || STATUS_TONE.Pending}`}
                              data-testid={`export-chip-${r.order_number}-${d}`}
                              title={rawStatus}
                            >
                              {displayedStatus}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          {isSuperAdmin && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); fetchDetail(r.id); }}
                                className="inline-flex items-center justify-center w-6 h-6 rounded-full text-amber-700 hover:bg-amber-100 transition"
                                data-testid={`export-row-edit-${r.order_number}`}
                                title="Edit export order (super admin)"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); deleteOrder(r); }}
                                className="inline-flex items-center justify-center w-6 h-6 rounded-full text-red-600 hover:bg-red-100 transition"
                                data-testid={`export-row-delete-${r.order_number}`}
                                title="Permanently delete order (super admin)"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleRowExpand(r.id); }}
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 transition"
                            data-testid={`export-row-expand-${r.order_number}`}
                            title={expandedRows[r.id] ? 'Collapse detail' : 'Expand detail'}
                          >
                            {expandedRows[r.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows[r.id] && (
                      <tr className="bg-slate-50/70 border-t border-neutral-100">
                        <td colSpan={DEPT_KEYS.length + 5} className="px-4 py-3">
                          <RowDetailAccordion row={r} enabledFields={columnPrefs.enabled_fields || []} availableFields={availableFields} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail side sheet */}
        {selectedId && (
          <DetailSheet
            orderId={selectedId}
            loading={detailLoading}
            detail={detail}
            onClose={() => { setSelectedId(null); setDetail(null); }}
            onStageSaved={async () => {
              // Aug 2026 — Hard re-fetch. `fetchDetail` sets
              // `detail = null` before hitting the API, so any
              // deleted timeline row is guaranteed gone from the
              // DOM before the fresh payload lands. Awaiting the
              // Promise here lets callers (e.g. UpdateRow.remove)
              // hold their `busy` flag until the UI has updated.
              await Promise.all([
                Promise.resolve(fetchList()),
                selectedId ? fetchDetail(selectedId) : Promise.resolve(),
              ]);
            }}
            authHeaders={authHeaders}
            toast={toast}
            displayDeptLabel={displayDeptLabel}
            displayStatusLabel={displayStatusLabel}
            isDeptHidden={isDeptHidden}
            visibleDeptKeys={visibleDeptKeys}
            deptOptions={exportConfig.department_options || {}}
          />
        )}
        {/* Share Timeline links modal (super admin) */}
        {shareOpen && (
          <ShareLinksModal
            onClose={() => setShareOpen(false)}
            authHeaders={authHeaders}
            toast={toast}
          />
        )}
        {/* Columns config dialog (super admin) */}
        {columnsDialogOpen && (
          <ColumnsConfigDialog
            available={availableFields}
            draft={columnsDraft}
            setDraft={setColumnsDraft}
            saving={columnsSaving}
            onSave={saveColumnsDialog}
            onClose={() => setColumnsDialogOpen(false)}
            authHeaders={authHeaders}
            toast={toast}
          />
        )}
        {/* Departments & Statuses config dialog (super admin) — hide,
            rename, or re-label statuses. Propagates to every view. */}
        {deptConfigOpen && (
          <ExportDashboardConfigDialog
            initial={exportConfig}
            authHeaders={authHeaders}
            toast={toast}
            onSaved={(next) => {
              setExportConfig(next);
              setDeptConfigOpen(false);
              // Refetch the list so any hidden depts drop from the
              // table immediately without waiting for the next poll.
              fetchList();
            }}
            onClose={() => setDeptConfigOpen(false)}
          />
        )}

        {newOrderOpen && (
          <NewExportOrderModal
            products={orderProducts}
            authHeaders={authHeaders}
            isSuperAdmin={isSuperAdmin}
            onClose={() => setNewOrderOpen(false)}
            onCreated={() => {
              setNewOrderOpen(false);
              // Refresh the timeline list so the new order lands
              // immediately.
              fetchList();
              toast({ title: 'Export order created' });
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// -------------------------------------------------------------------
// Feb 2026 — collapsible per-row detail accordion. Renders the fields
// the super admin selected in the Columns dialog. Fields with no data
// are silently omitted so a mostly-empty row doesn't display a wall
// of dashes.
// -------------------------------------------------------------------

const DEPT_KEY_ORDER = ['documentation', 'transport', 'accounts', 'logistics'];
// Legacy label kept so historical rows with department='clearing' still
// render sensibly; the ordered list above no longer includes it.
const DEPT_LABEL_MAP = {
  documentation: 'Documentation', clearing: 'Clearing',
  transport: 'Transport', accounts: 'Accounts', logistics: 'Logistics',
};

const RowDetailAccordion = ({ row, enabledFields, availableFields }) => {
  const enabled = Array.isArray(enabledFields) ? enabledFields : [];
  if (enabled.length === 0) {
    return (
      <div className="text-[11px] italic text-neutral-500 px-1">
        No fields selected. A super admin can pick fields via the <b>Columns</b> button above.
      </div>
    );
  }
  const labelByKey = Object.fromEntries((availableFields || []).map((f) => [f.key, f.label]));

  const money = (v) => `${Number(v || 0).toFixed(3)}`;

  const renderCell = (key) => {
    const label = labelByKey[key] || key;
    switch (key) {
      case 'order_number': return row.order_number ? { label, value: <span className="font-mono">{row.order_number}</span> } : null;
      case 'business_name': return row.business_name ? { label, value: row.business_name } : null;
      case 'customer_name': return row.customer_name ? { label, value: row.customer_name } : null;
      case 'customer_email': return row.customer_email ? { label, value: row.customer_email } : null;
      case 'customer_phone': return row.customer_phone ? { label, value: row.customer_phone } : null;
      case 'customer_number': return row.customer_number ? { label, value: <span className="font-mono">{row.customer_number}</span> } : null;
      case 'country': return row.country_name || row.country_code ? {
        label,
        value: (<span>{row.country_name || '—'}{row.country_code ? <span className="font-mono text-neutral-500 ml-1">({row.country_code})</span> : ''}</span>),
      } : null;
      case 'total': return { label, value: <b>{money(row.total)}{row.currency ? ` ${row.currency}` : ' OMR'}</b> };
      case 'currency': return row.currency ? { label, value: row.currency } : null;
      case 'notes': return row.notes ? { label, value: row.notes } : null;
      case 'status': return { label, value: <span className="uppercase font-bold text-[11px]">{row.status || 'new'}</span> };
      case 'created_at': return row.created_at ? { label, value: fmtDT(row.created_at) } : null;
      case 'shipped_at': return row.shipped_at ? { label, value: fmtDT(row.shipped_at) } : null;
      case 'item_count': return { label, value: row.item_count ?? (Array.isArray(row.items) ? row.items.length : 0) };
      case 'items_summary': {
        const items = Array.isArray(row.items) ? row.items : [];
        if (items.length === 0) return null;
        return {
          label,
          value: (
            <ul className="list-disc pl-4 text-[12px] leading-relaxed">
              {items.slice(0, 12).map((it, i) => (
                <li key={i}>
                  <span className="font-mono text-neutral-500">{it.sku || '—'}</span>{' '}
                  {it.name} × <b>{it.qty}</b>{' '}
                  <span className="text-neutral-500">{it.uom || 'ctn'}</span>
                </li>
              ))}
              {items.length > 12 && <li className="text-neutral-500">+{items.length - 12} more…</li>}
            </ul>
          ),
        };
      }
      case 'department_status': {
        const ds = row.department_status || {};
        return {
          label,
          value: (
            <div className="flex flex-wrap gap-1.5">
              {DEPT_KEY_ORDER.map((d) => {
                const st = (ds[d] || {}).status || 'Pending';
                const docs = ((ds[d] || {}).documents || []).length;
                return (
                  <span key={d} className="inline-flex items-center gap-1 rounded-full bg-white border border-neutral-200 px-2 py-0.5 text-[10px] font-semibold">
                    {DEPT_LABEL_MAP[d]} · <b>{st}</b>
                    {docs > 0 && <span className="text-neutral-500">· 📎{docs}</span>}
                  </span>
                );
              })}
            </div>
          ),
        };
      }
      case 'documents_uploaded': {
        const ds = row.department_status || {};
        const docs = [];
        DEPT_KEY_ORDER.forEach((d) => {
          ((ds[d] || {}).documents || []).forEach((doc) => {
            docs.push({ dept: DEPT_LABEL_MAP[d], name: doc.name || doc.filename || 'Document', url: doc.url || doc.web_view_link || '' });
          });
        });
        if (docs.length === 0) return null;
        return {
          label,
          value: (
            <ul className="list-disc pl-4 text-[12px] leading-relaxed">
              {docs.slice(0, 20).map((d, i) => (
                <li key={i}>
                  [{d.dept}]{' '}
                  {d.url
                    ? <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-[#0078D4] hover:underline">{d.name}</a>
                    : d.name}
                </li>
              ))}
            </ul>
          ),
        };
      }
      default: return null;
    }
  };

  const cells = enabled.map(renderCell).filter(Boolean);
  if (cells.length === 0) {
    return <div className="text-[11px] italic text-neutral-500 px-1">No data for the selected fields on this order.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
      {cells.map((c, i) => (
        <div key={i} className="min-w-0" data-testid={`export-row-detail-cell-${i}`}>
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-0.5">{c.label}</div>
          <div className="text-[12px] text-[#0F172A] break-words">{c.value}</div>
        </div>
      ))}
    </div>
  );
};

// -------------------------------------------------------------------
// Departments & Statuses configuration (super admin only)
// -------------------------------------------------------------------
// Persists overrides to `SiteSettings.export_dashboard_config`:
//   * hidden_departments  — hide any of the four active stages from
//     non-super-admin + public views.
//   * department_labels   — rename per-dept label (e.g. Documentation → Docs)
//   * status_labels       — rename per-status label
//     (In Progress → Working, Approved → Verified).
// Super admin still sees the raw technical values everywhere; only
// non-super callers + the public share view apply the transform.
const ExportDashboardConfigDialog = ({ initial, authHeaders, toast, onSaved, onClose }) => {
  const [hidden, setHidden] = useState(new Set(initial?.hidden_departments || []));
  const [deptLabels, setDeptLabels] = useState({ ...(initial?.department_labels || {}) });
  const [statusLabels, setStatusLabels] = useState({ ...(initial?.status_labels || {}) });
  // Feb 2026 — Per-dept dropdown option lists (updates + notes).
  // Deep-cloned so the local editor state doesn't leak back into the
  // parent's `exportConfig` until Save fires.
  const [deptOptions, setDeptOptions] = useState(() => {
    const seed = initial?.department_options || {};
    const out = {};
    DEPT_KEYS.forEach((k) => {
      const src = seed[k] || {};
      out[k] = {
        updates: [...(src.updates || [])],
        notes: [...(src.notes || [])],
      };
    });
    return out;
  });
  // Which dept's "add" row is currently accepting a new entry.
  // Shape: { updates: { <dept>: 'draft text' }, notes: { <dept>: 'draft text' } }
  const [drafts, setDrafts] = useState({ updates: {}, notes: {} });
  const [tab, setTab] = useState('depts');  // 'depts' | 'options'
  const [saving, setSaving] = useState(false);

  const toggleHidden = (dkey) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(dkey)) next.delete(dkey); else next.add(dkey);
    return next;
  });

  const setDeptLabel = (dkey, value) => setDeptLabels((prev) => {
    const next = { ...prev };
    const trimmed = value.trim();
    if (!trimmed || trimmed === DEPT_LABEL[dkey]) delete next[dkey];
    else next[dkey] = trimmed;
    return next;
  });
  const setStatusLabel = (status, value) => setStatusLabels((prev) => {
    const next = { ...prev };
    const trimmed = value.trim();
    if (!trimmed || trimmed === status) delete next[status];
    else next[status] = trimmed;
    return next;
  });

  const resetAll = () => {
    setHidden(new Set());
    setDeptLabels({});
    setStatusLabels({});
    const empty = {};
    DEPT_KEYS.forEach((k) => { empty[k] = { updates: [], notes: [] }; });
    setDeptOptions(empty);
    setDrafts({ updates: {}, notes: {} });
  };

  const addOption = (dkey, slot) => {
    const raw = (drafts[slot][dkey] || '').trim();
    if (!raw) return;
    setDeptOptions((prev) => {
      const list = prev[dkey]?.[slot] || [];
      // Case-insensitive dedupe, preserve original insertion order.
      if (list.some((v) => v.toLowerCase() === raw.toLowerCase())) return prev;
      return {
        ...prev,
        [dkey]: { ...prev[dkey], [slot]: [...list, raw] },
      };
    });
    setDrafts((prev) => ({ ...prev, [slot]: { ...prev[slot], [dkey]: '' } }));
  };
  const removeOption = (dkey, slot, value) => {
    setDeptOptions((prev) => ({
      ...prev,
      [dkey]: {
        ...prev[dkey],
        [slot]: (prev[dkey]?.[slot] || []).filter((v) => v !== value),
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip empty dept entries so the sanitiser doesn't need to.
      const trimmedOptions = {};
      Object.entries(deptOptions).forEach(([k, v]) => {
        const entry = {};
        if ((v.updates || []).length) entry.updates = v.updates;
        if ((v.notes || []).length) entry.notes = v.notes;
        if (Object.keys(entry).length) trimmedOptions[k] = entry;
      });
      const payload = {
        hidden_departments: Array.from(hidden),
        department_labels: deptLabels,
        status_labels: statusLabels,
        department_options: trimmedOptions,
      };
      const { data } = await axios.put(
        `${API}/admin/export-dashboard/config`,
        payload,
        { headers: authHeaders },
      );
      toast && toast({
        title: 'Departments updated',
        description: 'Renamed / hidden departments and dropdown options now apply across every view.',
      });
      onSaved && onSaved({
        hidden_departments: data.hidden_departments || [],
        department_labels: data.department_labels || {},
        status_labels: data.status_labels || {},
        department_options: data.department_options || {},
      });
    } catch (err) {
      toast && toast({
        title: 'Could not save',
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" data-testid="export-dept-config-dialog">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">Departments &amp; Statuses</h2>
            <p className="text-[11px] text-neutral-500 mt-0.5">
              Rename departments, hide any stage from non-admin + public views, or re-label the stage statuses. Super admin always sees the original values.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-neutral-100" data-testid="export-dept-config-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pt-4">
          {/* Feb 2026 — Two-tab layout: department + status label
              controls on the left tab, per-department dropdown
              option lists on the right. Keeps the modal compact. */}
          <div className="inline-flex rounded-full border border-neutral-200 bg-neutral-50 p-0.5 text-[11px] font-semibold">
            <button
              type="button"
              onClick={() => setTab('depts')}
              className={`px-3 py-1 rounded-full transition ${tab === 'depts' ? 'bg-white shadow text-[#0F172A]' : 'text-neutral-500 hover:text-[#0F172A]'}`}
              data-testid="export-dept-config-tab-depts"
            >
              Departments & Statuses
            </button>
            <button
              type="button"
              onClick={() => setTab('options')}
              className={`px-3 py-1 rounded-full transition ${tab === 'options' ? 'bg-white shadow text-[#0F172A]' : 'text-neutral-500 hover:text-[#0F172A]'}`}
              data-testid="export-dept-config-tab-options"
            >
              Dropdown options
            </button>
          </div>
        </div>

        {tab === 'depts' && (
        <div className="px-6 py-4 space-y-5" data-testid="export-dept-config-pane-depts">
          {/* Departments — visibility + label */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">
              Departments (5 stages)
            </div>
            <div className="space-y-2">
              {DEPT_KEYS.map((dkey) => {
                const isHidden = hidden.has(dkey);
                const currentLabel = deptLabels[dkey] || '';
                return (
                  <div key={dkey} className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleHidden(dkey)}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold border transition ${isHidden ? 'bg-neutral-100 text-neutral-500 border-neutral-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
                      data-testid={`export-dept-visibility-${dkey}`}
                      title={isHidden ? 'Hidden from non-admin + public views' : 'Visible to everyone'}
                    >
                      {isHidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      {isHidden ? 'Hidden' : 'Visible'}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">{DEPT_LABEL[dkey]}</div>
                      <input
                        type="text"
                        value={currentLabel}
                        onChange={(e) => setDeptLabel(dkey, e.target.value)}
                        placeholder={`Rename to… (default: ${DEPT_LABEL[dkey]})`}
                        className="w-full mt-0.5 text-[13px] text-[#0F172A] border-0 border-b border-transparent focus:border-[#0078D4] focus:outline-none py-0.5 bg-transparent"
                        data-testid={`export-dept-label-${dkey}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Statuses — rename */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">
              Status labels (per-department dropdown)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {STAGE_STATUSES.map((s) => (
                <div key={s} className="rounded-xl border border-neutral-200 bg-white px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">{s}</div>
                  <input
                    type="text"
                    value={statusLabels[s] || ''}
                    onChange={(e) => setStatusLabel(s, e.target.value)}
                    placeholder={`Rename to… (default: ${s})`}
                    className="w-full mt-0.5 text-[13px] text-[#0F172A] border-0 border-b border-transparent focus:border-[#0078D4] focus:outline-none py-0.5 bg-transparent"
                    data-testid={`export-status-label-${s.replace(/\s+/g, '-').toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-neutral-500 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
            <b>Note:</b> Changes apply <em>immediately</em> across the dashboard table, order detail cards, timeline history, and public share view. Super admin always sees the original technical values (e.g. <span className="font-mono">documentation</span> · <span className="font-mono">In Progress</span>).
          </div>
        </div>
        )}

        {tab === 'options' && (
        <div className="px-6 py-4 space-y-4" data-testid="export-dept-config-pane-options">
          <div className="text-[11px] text-neutral-500 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
            Department users pick from these lists when posting a stage update or a daily update. At least one selection is required to post. Free-text is not allowed &mdash; only super admin manages the options.
          </div>
          {DEPT_KEYS.map((dkey) => {
            const opts = deptOptions[dkey] || { updates: [], notes: [] };
            return (
              <div key={dkey} className="rounded-xl border border-neutral-200 bg-white p-3" data-testid={`export-dept-options-${dkey}`}>
                <div className="text-sm font-bold text-[#0F172A] mb-2">{deptLabels[dkey] || DEPT_LABEL[dkey]}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {['updates', 'notes'].map((slot) => (
                    <div key={slot} className="rounded-lg border border-neutral-100 bg-neutral-50 p-2">
                      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1.5">
                        {slot === 'updates' ? 'Update options' : 'Note options'}
                      </div>
                      {(opts[slot] || []).length === 0 && (
                        <div className="text-[10px] text-neutral-400 italic py-1">No entries yet.</div>
                      )}
                      <div className="space-y-1">
                        {(opts[slot] || []).map((v) => (
                          <div key={v} className="flex items-center justify-between gap-2 rounded-md bg-white border border-neutral-200 px-2 py-1">
                            <span className="text-[12px] text-[#0F172A] truncate" title={v}>{v}</span>
                            <button
                              type="button"
                              onClick={() => removeOption(dkey, slot, v)}
                              className="text-[10px] text-red-600 hover:text-red-700 font-semibold"
                              data-testid={`export-dept-option-remove-${dkey}-${slot}-${v.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <input
                          type="text"
                          value={drafts[slot][dkey] || ''}
                          onChange={(e) => setDrafts((prev) => ({ ...prev, [slot]: { ...prev[slot], [dkey]: e.target.value } }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(dkey, slot); } }}
                          placeholder={slot === 'updates' ? 'e.g. Customs filed' : 'e.g. Awaiting POD'}
                          className="flex-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs"
                          data-testid={`export-dept-option-input-${dkey}-${slot}`}
                          maxLength={120}
                        />
                        <button
                          type="button"
                          onClick={() => addOption(dkey, slot)}
                          className="rounded-md bg-[#0078D4] text-white px-2 py-1 text-[11px] font-semibold hover:bg-[#106EBE]"
                          data-testid={`export-dept-option-add-${dkey}-${slot}`}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        )}


        <div className="px-6 py-3 border-t bg-neutral-50 flex items-center justify-between sticky bottom-0">
          <button
            type="button"
            onClick={resetAll}
            className="text-[11px] text-neutral-600 hover:text-[#0078D4] underline underline-offset-2"
            data-testid="export-dept-config-reset"
          >
            Restore defaults
          </button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="export-dept-config-cancel">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#0078D4] hover:bg-[#005A9E]"
              data-testid="export-dept-config-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};


// -------------------------------------------------------------------
// Columns configuration dialog (super admin only).
// -------------------------------------------------------------------

const ColumnsConfigDialog = ({ available, draft, setDraft, saving, onSave, onClose, authHeaders, toast }) => {
  const grouped = useMemo(() => {
    const buckets = {};
    (available || []).forEach((f) => {
      const g = f.group || 'misc';
      buckets[g] = buckets[g] || [];
      buckets[g].push(f);
    });
    return buckets;
  }, [available]);
  const groupTitles = { identity: 'Identity', commercial: 'Commercial', workflow: 'Workflow', audit: 'Audit trail', misc: 'Other' };
  const toggle = (key) => {
    setDraft((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  // Aug 2026 — Global "hide Unit Price + Total on Items table for
  // non-super-admin users" toggle. Backed by
  // `SiteSettings.export_items_show_prices`. Loaded lazily so the
  // dialog opens fast, then written back via PUT /admin/settings.
  const [showPrices, setShowPrices] = useState(null); // null=loading, true/false=known
  const [savingPrices, setSavingPrices] = useState(false);

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/settings`, { headers: authHeaders });
        if (!cancel) setShowPrices(data?.export_items_show_prices !== false);
      } catch {
        if (!cancel) setShowPrices(true); // fail-open: matches server default
      }
    })();
    return () => { cancel = true; };
  }, [authHeaders]);

  const togglePriceVisibility = async () => {
    if (showPrices === null || savingPrices) return;
    const next = !showPrices;
    setSavingPrices(true);
    try {
      // Merge over the whole settings doc — the PUT endpoint accepts
      // the full model, so re-send the current state with the one
      // field flipped.
      const { data: current } = await axios.get(`${API}/admin/settings`, { headers: authHeaders });
      await axios.put(
        `${API}/admin/settings`,
        { ...current, export_items_show_prices: next },
        { headers: authHeaders },
      );
      setShowPrices(next);
      toast && toast({
        title: next ? 'Prices visible to all' : 'Prices hidden from non-admins',
        description: next
          ? 'Unit Price + Total will render on Items tables for every user.'
          : 'Only super admins will see Unit Price + Total from now on.',
      });
    } catch (err) {
      toast && toast({
        title: 'Could not save',
        description: err?.response?.data?.detail || err?.message,
      });
    } finally { setSavingPrices(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" data-testid="export-columns-dialog">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">Row detail columns</h2>
            <p className="text-[11px] text-neutral-500 mt-0.5">Pick which fields render inside the collapsible detail on every row (visible to the whole team + public timeline).</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-neutral-100" data-testid="export-columns-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-5">
          {/* Aug 2026 — super-admin only global toggle. Controls
              whether Unit Price + Total render on the Items table
              for non-super users on EVERY export order. */}
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-[#0F172A] flex items-center gap-1.5">
                  <Lock className="w-4 h-4 text-amber-700" />
                  Item Unit Price + Total visibility
                </div>
                <p className="text-[11px] text-neutral-600 mt-1 max-w-md">
                  When off, non-super-admin users won&apos;t see the Unit Price and Total columns on any export order&apos;s Items table. Super admins always see them.
                </p>
              </div>
              <button
                type="button"
                onClick={togglePriceVisibility}
                disabled={showPrices === null || savingPrices}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition ${showPrices ? 'bg-emerald-600' : 'bg-neutral-300'} disabled:opacity-40`}
                aria-pressed={!!showPrices}
                data-testid="export-items-show-prices-toggle"
              >
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${showPrices ? 'translate-x-5' : 'translate-x-0.5'} translate-y-0.5`}
                />
              </button>
            </div>
            <div className="text-[11px] mt-2 font-semibold" data-testid="export-items-show-prices-state">
              {showPrices === null
                ? <span className="text-neutral-500">Loading…</span>
                : showPrices
                  ? <span className="text-emerald-700">Visible to all users</span>
                  : <span className="text-neutral-700">Hidden from non-admins</span>}
            </div>
          </div>

          {Object.entries(grouped).map(([group, fields]) => (
            <div key={group}>
              <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">
                {groupTitles[group] || group}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {fields.map((f) => (
                  <label
                    key={f.key}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-[13px] transition ${draft.includes(f.key) ? 'bg-[#DEECF9] border-[#0078D4] text-[#0F172A]' : 'bg-white border-neutral-200 hover:bg-neutral-50'}`}
                    data-testid={`export-columns-field-${f.key}`}
                  >
                    <input
                      type="checkbox"
                      className="accent-[#0078D4]"
                      checked={draft.includes(f.key)}
                      onChange={() => toggle(f.key)}
                    />
                    <span>{f.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t bg-neutral-50 flex items-center justify-between sticky bottom-0">
          <div className="text-[11px] text-neutral-500">{draft.length} field{draft.length === 1 ? '' : 's'} selected</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="export-columns-cancel">Cancel</Button>
            <Button onClick={onSave} disabled={saving} className="bg-[#0078D4] hover:bg-[#005A9E]" data-testid="export-columns-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryTile = ({ label, value, tone, testid }) => {
  const bg = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    neutral: 'bg-neutral-50 border-neutral-200 text-neutral-800',
  }[tone] || 'bg-white border-neutral-200 text-[#0F172A]';
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value ?? 0}</div>
    </div>
  );
};

const DetailSheet = ({
  orderId, loading, detail, onClose, onStageSaved, authHeaders, toast,
  displayDeptLabel, displayStatusLabel, isDeptHidden, visibleDeptKeys,
  deptOptions,
}) => {
  // Fallbacks so DetailSheet keeps working if a caller forgets to pass
  // the config helpers (e.g. legacy tests). Behaviour reverts to
  // showing raw DEPT_LABEL / STAGE_STATUSES values in that case.
  const _dept = displayDeptLabel || ((k) => DEPT_LABEL[k] || k);
  const _stat = displayStatusLabel || ((s) => s);
  const _hidden = isDeptHidden || (() => false);
  const _visibleKeys = visibleDeptKeys || DEPT_KEYS;
  // Feb 2026 — "shipped" for the read-only banner covers every
  // terminal-complete status (shipped / delivered / received /
  // completed) so non-super-admins can't edit any Shipped-tab
  // order regardless of which terminal status ops chose.
  const SHIPPED_STATUSES = ['shipped', 'delivered', 'received', 'completed'];
  const isShipped = SHIPPED_STATUSES.includes(String(detail?.status || '').toLowerCase());
  const isSuperAdmin = !!detail?.is_super_admin;
  const readOnlyBanner = isShipped && !isSuperAdmin;
  // Super admin PDF toggle — regular users always get timeline in PDF;
  // super admin can flip it off before downloading.
  const [includeTimelineInPdf, setIncludeTimelineInPdf] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const downloadPdf = async () => {
    if (!orderId) return;
    setDownloadingPdf(true);
    try {
      const inc = isSuperAdmin ? includeTimelineInPdf : true;
      const res = await axios.get(`${API}/orders/${orderId}/invoice`, {
        headers: authHeaders,
        responseType: 'blob',
        params: { include_activity: inc ? 'true' : 'false' },
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${detail?.order_number || orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'PDF download failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="export-detail-sheet">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-4 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-xs text-neutral-500 uppercase tracking-wider font-bold">Export order</div>
            <div className="text-lg font-bold text-[#0F172A]">
              {detail?.order_number || '…'}
              {isShipped && (
                <span className="ml-2 inline-flex items-center rounded-full bg-emerald-700 text-white px-2 py-0.5 text-[10px] font-bold tracking-wider">
                  <Ship className="w-3 h-3 mr-1" /> SHIPPED
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {detail && (
              <div className="flex items-center gap-2" data-testid="export-pdf-controls">
                {isSuperAdmin && (
                  <label className="inline-flex items-center gap-1.5 text-[11px] text-neutral-600 select-none" title="Super admin only">
                    <input
                      type="checkbox"
                      checked={includeTimelineInPdf}
                      onChange={(e) => setIncludeTimelineInPdf(e.target.checked)}
                      data-testid="export-pdf-include-timeline"
                    />
                    Timeline
                  </label>
                )}
                <Button
                  onClick={downloadPdf}
                  disabled={downloadingPdf}
                  variant="outline"
                  size="sm"
                  className="rounded-full h-8 text-xs"
                  data-testid="export-detail-download-pdf"
                >
                  {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                  PDF
                </Button>
              </div>
            )}
            <Button variant="ghost" onClick={onClose} className="rounded-full h-8" data-testid="export-detail-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading && (
          <div className="p-6 text-center text-sm text-neutral-500" data-testid="export-detail-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
          </div>
        )}

        {!loading && detail && (
          <div className="p-4 space-y-4">
            {readOnlyBanner && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2" data-testid="export-detail-locked-banner">
                <Lock className="w-3.5 h-3.5" /> This order is <b>SHIPPED</b> and read-only. Contact super admin for edits.
              </div>
            )}

            {isSuperAdmin && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-center gap-2" data-testid="export-detail-super-banner">
                <Shield className="w-3.5 h-3.5" />
                <span><b>Super Admin Override active</b> — every field on every stage is editable regardless of order status.</span>
              </div>
            )}

            {/* Order-level editor (super admin only) — falls back to a
                read-only summary block for other roles. */}
            <SuperAdminOrderEditor
              detail={detail}
              enabled={isSuperAdmin}
              orderId={orderId}
              authHeaders={authHeaders}
              toast={toast}
              onSaved={onStageSaved}
            />

            {/* Aug 2026 — Items table renders BELOW the summary panel.
                Non-super-admin users only see Unit Price + Total when
                `detail.export_items_show_prices` is True (the setting
                super admins toggle from the dashboard's Columns
                dialog). */}
            <ExportItemsPanel
              detail={detail}
              orderId={orderId}
              authHeaders={authHeaders}
              toast={toast}
              onNotified={onStageSaved}
              isSuperAdmin={isSuperAdmin}
            />

            {/* Aug 2026 — Unified Daily Updates panel is the SOLE
                input surface for every department. The per-dept
                StageEditor cards were retired; the Status dropdown
                and Upload button live inside this composer now, so
                a single "Post" click can flip stage status AND
                append a timeline entry in one write. */}
            <DailyUpdatesPanel
              detail={detail}
              orderId={orderId}
              authHeaders={authHeaders}
              toast={toast}
              onChanged={onStageSaved}
              deptOptions={deptOptions || {}}
              displayDeptLabel={_dept}
              displayStatusLabel={_stat}
              visibleDeptKeys={_visibleKeys}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// SuperAdminOrderEditor — Feb 2026
// Renders the order-level summary panel. When `enabled` (super admin
// only) every field becomes an editable input + a Save button posts a
// PATCH to `/api/admin/export-orders/{id}`. For non-super-admins we
// keep the historical read-only summary so nothing regresses.
// ============================================================
const SuperAdminOrderEditor = ({ detail, enabled, orderId, authHeaders, toast, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setForm({
      customer_business_name: detail.business_name || '',
      customer_name: detail.contact_name || '',
      customer_email: detail.customer_email || '',
      customer_phone: detail.customer_phone || '',
      customer_address: detail.customer_address || '',
      export_country_code: detail.country_code || '',
      export_country_name: detail.country_name || '',
      notes: detail.notes || '',
      status: detail.status || '',
    });
    setEdit(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Feb 2026 — Always stamp `is_export_order: true` on the edit
      // payload so downstream triggers (plant auto-notify, timeline
      // mirror, etc.) recognise this as an export order even when
      // the caller only touched customer / country / status fields.
      // The backend endpoint already filters by
      // `is_export_order: True`, but the explicit flag prevents any
      // future generic-order code path from mis-routing this write.
      const payload = { ...form, is_export_order: true };
      const { data } = await axios.patch(
        `${API}/admin/export-orders/${orderId}`,
        payload,
        { headers: authHeaders },
      );
      toast({
        title: 'Order updated',
        description: (data?.changed_fields || []).length
          ? `Updated: ${(data.changed_fields || []).join(', ')}`
          : 'No changes',
      });
      setEdit(false);
      onSaved && onSaved();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setSaving(false);
    }
  };

  if (!enabled || !edit) {
    // Read-only summary block — same visual as before, plus an "Edit"
    // button when the caller is super admin so they can toggle into
    // form mode.
    return (
      <div className="grid grid-cols-2 gap-3 rounded-xl border border-neutral-200 p-3 text-xs relative" data-testid="export-detail-summary">
        {enabled && (
          <button
            type="button"
            onClick={startEdit}
            className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-2 py-0.5 text-[10px] font-bold"
            data-testid="export-super-edit-toggle"
          >
            <Edit3 className="w-3 h-3" /> Edit fields
          </button>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Business</div>
          <CustomerNameLabel
            customer={{ business_name: detail.business_name, name: detail.contact_name }}
            secondaryClassName="text-neutral-500 text-sm"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Country</div>
          <div className="text-[#0F172A]">
            {detail.country_code && <span className="font-mono bg-neutral-100 rounded px-1 py-0.5 mr-1">{detail.country_code}</span>}
            {detail.country_name || '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Total</div>
          <div className="text-[#0F172A] font-semibold"><Rial /> {(detail.total || 0).toFixed(3)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Order Status</div>
          <div className="text-[#0F172A]">{detail.status || '—'}</div>
        </div>
      </div>
    );
  }

  // Edit mode — super admin only.
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/40 p-3 space-y-2" data-testid="export-super-edit-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" /> Super Admin Edit Mode
        </div>
        <button
          type="button"
          onClick={() => setEdit(false)}
          className="text-[11px] text-neutral-500 hover:text-neutral-900"
          data-testid="export-super-edit-cancel"
        >
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Field label="Business Name" testid="super-edit-business">
          <Input value={form.customer_business_name} onChange={(e) => setForm({ ...form, customer_business_name: e.target.value })} />
        </Field>
        <Field label="Contact Person" testid="super-edit-contact">
          <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
        </Field>
        <Field label="Email" testid="super-edit-email">
          <Input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
        </Field>
        <Field label="Phone" testid="super-edit-phone">
          <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
        </Field>
        <Field label="Country Code" testid="super-edit-country-code">
          <Input value={form.export_country_code} onChange={(e) => setForm({ ...form, export_country_code: e.target.value.toUpperCase() })} />
        </Field>
        <Field label="Country Name" testid="super-edit-country-name">
          <Input value={form.export_country_name} onChange={(e) => setForm({ ...form, export_country_name: e.target.value })} />
        </Field>
        <Field label="Order Status" testid="super-edit-status">
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {['new', 'confirmed', 'assigned', 'picked_up', 'out_for_delivery', 'delivered', 'shipped', 'cancelled', 'failed', 'draft'].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Address" testid="super-edit-address">
          <Input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Order Notes" testid="super-edit-notes">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          size="sm"
          className="rounded-full h-8 text-xs bg-amber-700 hover:bg-amber-800 text-white"
          data-testid="export-super-edit-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save Order
        </Button>
      </div>
    </div>
  );
};

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide block mb-0.5">{label}</label>
    {children}
  </div>
);

// ============================================================
// ExportItemsPanel — Aug 2026
// Lists the order's items. For any row whose ordered qty exceeds the
// currently-available product stock, renders a "Notify Plant" button
// that opens a confirmation dialog and POSTs to the notify-plant
// endpoint. Already-notified rows (per `detail.plant_notifications`)
// show a "Notified · <date>" chip instead so the button can't fire
// twice for the same item.
// ============================================================
const ExportItemsPanel = ({ detail, orderId, authHeaders, toast, onNotified, isSuperAdmin }) => {
  const [products, setProducts] = useState({});
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null); // {item, product, shortfall}
  const [notifying, setNotifying] = useState(false);

  const items = Array.isArray(detail?.items) ? detail.items : [];
  const plantEmail = (detail?.plant_team_email || '').trim();
  // Aug 2026 — Category-keyed mailing lists. Super admins get the full
  // recipient arrays; non-super callers get `{count}` objects (privacy).
  // The panel derives which list will fire from each row's category
  // (fetched with the product doc) so the modal can preview it.
  const mailingLists = detail?.plant_team_mailing_lists || {};
  const notifications = detail?.plant_notifications || {};
  const showPrices = !!isSuperAdmin || detail?.export_items_show_prices !== false;

  const listInfoFor = (catSlug) => {
    const key = (catSlug || '').trim().toLowerCase();
    if (!key) return { key: '', label: 'Unknown', addresses: [], count: 0, configured: false, hasCategory: false };
    const entry = mailingLists[key];
    if (Array.isArray(entry)) {
      const clean = entry.filter((a) => String(a || '').trim());
      return { key, addresses: clean, count: clean.length, configured: clean.length > 0, hasCategory: true };
    }
    if (entry && typeof entry === 'object' && typeof entry.count === 'number') {
      return { key, addresses: [], count: entry.count, configured: entry.count > 0, hasCategory: true };
    }
    return { key, addresses: [], count: 0, configured: false, hasCategory: false };
  };

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/products`, { headers: authHeaders });
        if (cancel) return;
        const list = Array.isArray(data) ? data : (data?.products || []);
        const map = {};
        list.forEach((p) => { if (p?.id) map[p.id] = p; });
        setProducts(map);
      } catch {
        /* Stock lookup best-effort. Without it the panel just renders
           without any Notify Plant buttons (defensive). */
      } finally {
        if (!cancel) setProductsLoaded(true);
      }
    })();
    return () => { cancel = true; };
  }, [authHeaders]);

  if (!items.length) return null;

  const availableOf = (p) => {
    if (!p) return 0;
    const v = (p.available_qty !== undefined && p.available_qty !== null) ? p.available_qty : p.stock_qty;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  };

  // Feb 2026 — Own-order allocation attribution. `available_qty` on the
  // product doc is `stock_qty − Σ placement.allocated_qty` across every
  // ACTIVE order, INCLUDING this one. Without this add-back, an order's
  // own confirmed allocations get subtracted twice on its own view
  // (once from `available_qty`, once via the item qty on the same row)
  // and produce a phantom shortfall. The backend publishes the map so
  // this stays a simple lookup here.
  const ownAllocByPid = (detail && detail.own_allocated_by_pid) || {};
  const ownAllocOf = (pid) => {
    if (!pid) return 0;
    const n = Number(ownAllocByPid[pid] || 0);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  };

  const rows = items.map((it, idx) => {
    const pid = it.id || it.product_id;
    const prod = pid ? products[pid] : null;
    const qty = Number(it.qty || 0) || 0;
    const rawAvail = prod ? availableOf(prod) : 0;
    // Add the order's OWN allocated qty back — it's already committed
    // to this order, so it should count as "in stock" against this
    // order's requirement.
    const avail = prod ? rawAvail + ownAllocOf(pid) : 0;
    const shortfall = productsLoaded && prod ? Math.max(0, qty - avail) : 0;
    const code = (it.sku || it.item_code || prod?.item_code || prod?.sku || '').trim();
    const name = (it.name || prod?.name || '').trim();
    const packSize = (it.size || it.pack_size || prod?.size || prod?.pack_size || '').toString().trim();
    const unitPrice = Number(it.price || 0);
    const total = unitPrice * qty;
    const notified = pid ? notifications[pid] : null;
    const catSlug = (prod?.category_slug || it.category_slug || '').toString().trim().toLowerCase();
    const listInfo = listInfoFor(catSlug);
    return {
      idx, pid, code, name, packSize, qty, avail, shortfall, unitPrice, total,
      hasShortfall: shortfall > 0,
      notified,
      catSlug,
      listInfo,
    };
  });

  const grandTotal = rows.reduce((acc, r) => acc + r.total, 0);

  const askConfirm = (row) => {
    // Aug 2026 — Notify Plant now routes via category. The modal
    // itself shows the routing decision + "no mailing list configured"
    // warning so ops can see WHY nothing would be sent before they
    // fire it. We ALWAYS open the modal (with a disabled Send button
    // when the list is empty) — that way super admins can click
    // through to the settings link inline.
    setConfirmItem(row);
  };

  const doNotify = async () => {
    if (!confirmItem?.pid) return;
    setNotifying(true);
    try {
      await axios.post(
        `${API}/admin/export-orders/${orderId}/items/${confirmItem.pid}/notify-plant`,
        {},
        { headers: authHeaders },
      );
      toast({
        title: 'Plant notified',
        description: `${confirmItem.code || confirmItem.name} × ${confirmItem.qty} — email dispatched.`,
      });
      setConfirmItem(null);
      onNotified && onNotified();
    } catch (err) {
      toast({
        title: 'Notify failed',
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setNotifying(false);
    }
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3" data-testid="export-items-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-bold text-[#0F172A] flex items-center gap-1.5">
          <Package2 className="w-4 h-4 text-[#0078D4]" /> Items ({items.length})
        </div>
        <div className="flex items-center gap-2">
          {!showPrices && (
            <div
              className="text-[10px] text-neutral-500 bg-neutral-100 border border-neutral-200 rounded-full px-2 py-0.5"
              title="Super admin has hidden Unit Price + Total from non-admin users"
            >
              Prices hidden
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
            <tr className="border-b border-neutral-200">
              <th className="text-left py-1.5 pr-2">Item Code</th>
              <th className="text-left py-1.5 pr-2">Item Name</th>
              <th className="text-left py-1.5 pr-2">Pack Size</th>
              <th className="text-right py-1.5 pr-2">Quantity</th>
              {showPrices && <th className="text-right py-1.5 pr-2">Unit Price</th>}
              {showPrices && <th className="text-right py-1.5 pr-2">Total</th>}
              <th className="text-right py-1.5 pr-2">In stock</th>
              <th className="text-right py-1.5 pr-2">Shortfall</th>
              <th className="text-right py-1.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {rows.map((r) => (
              <tr key={r.pid || r.idx} data-testid={`export-item-row-${r.pid || r.idx}`}>
                <td className="py-2 pr-2 font-mono">{r.code || '—'}</td>
                <td className="py-2 pr-2 font-semibold text-[#0F172A]">{r.name || '—'}</td>
                <td className="py-2 pr-2 text-neutral-600">{r.packSize || '—'}</td>
                <td className="py-2 pr-2 text-right tabular-nums">{r.qty}</td>
                {showPrices && <td className="py-2 pr-2 text-right tabular-nums" data-testid={`export-item-unit-price-${r.pid}`}>{r.unitPrice.toFixed(3)}</td>}
                {showPrices && <td className="py-2 pr-2 text-right tabular-nums font-semibold" data-testid={`export-item-total-${r.pid}`}>{r.total.toFixed(3)}</td>}
                <td className={`py-2 pr-2 text-right tabular-nums ${r.hasShortfall ? 'text-amber-700' : 'text-neutral-600'}`}>
                  {productsLoaded ? r.avail : '…'}
                </td>
                <td className={`py-2 pr-2 text-right tabular-nums font-bold ${r.hasShortfall ? 'text-red-700' : 'text-neutral-400'}`}>
                  {r.hasShortfall ? r.shortfall : '—'}
                </td>
                <td className="py-2 text-right">
                  {r.notified ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-0.5 text-[10px] font-bold"
                      title={`Notified by ${r.notified.notified_by} on ${fmtDT(r.notified.notified_at)}`}
                      data-testid={`export-item-notified-chip-${r.pid}`}
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Notified · {fmtDT(r.notified.notified_at)}
                    </span>
                  ) : r.hasShortfall ? (
                    <button
                      type="button"
                      onClick={() => askConfirm(r)}
                      className="inline-flex items-center gap-1 rounded-full bg-red-600 hover:bg-red-700 text-white px-2.5 py-1 text-[11px] font-bold shadow-sm disabled:opacity-40"
                      data-testid={`export-item-notify-plant-btn-${r.pid}`}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      Notify Plant
                    </button>
                  ) : (
                    <span className="text-[10px] text-neutral-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {showPrices && rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-neutral-300">
                <td colSpan={5} className="py-2 pr-2 text-right text-[11px] uppercase tracking-wider font-bold text-neutral-500">Grand total</td>
                <td className="py-2 pr-2 text-right font-bold tabular-nums text-[#0F172A]" data-testid="export-items-grand-total">
                  {grandTotal.toFixed(3)}
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {confirmItem && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => !notifying && setConfirmItem(null)}
          data-testid="notify-plant-confirm-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const li = confirmItem.listInfo || { configured: false, hasCategory: false };
              const canSend = li.configured || !!plantEmail;
              return (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${canSend ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                      <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-base font-bold text-[#0F172A]">Notify Plant of Shortfall?</div>
                      <div className="text-xs text-neutral-500">
                        {li.configured
                          ? <>Routing to the <b className="capitalize">{li.key}</b> mailing list · {li.count} recipient{li.count === 1 ? '' : 's'}</>
                          : (!li.hasCategory || !li.key)
                            ? <>Item has no category — using the legacy plant email.</>
                            : <>No mailing list configured for the <b className="capitalize">{li.key}</b> category.</>}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm space-y-1.5">
                    <div><span className="text-neutral-500 text-xs">Item code:</span> <b className="font-mono">{confirmItem.code || '—'}</b></div>
                    <div><span className="text-neutral-500 text-xs">Item name:</span> <b>{confirmItem.name || '—'}</b></div>
                    <div><span className="text-neutral-500 text-xs">Pack size:</span> <b>{confirmItem.packSize || '—'}</b></div>
                    <div><span className="text-neutral-500 text-xs">Category:</span> <b className="capitalize">{confirmItem.catSlug || '—'}</b></div>
                    <div><span className="text-neutral-500 text-xs">Required qty:</span> <b>{confirmItem.qty}</b></div>
                    <div><span className="text-neutral-500 text-xs">Currently in stock:</span> <span className="text-amber-700 font-semibold">{confirmItem.avail}</span></div>
                    <div><span className="text-neutral-500 text-xs">Shortfall:</span> <b className="text-red-700">{confirmItem.shortfall}</b></div>
                  </div>
                  {li.configured && li.addresses.length > 0 && (
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5" data-testid="notify-plant-recipients">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1">Recipients</div>
                      <div className="flex flex-wrap gap-1">
                        {li.addresses.map((a) => (
                          <span key={a} className="inline-block bg-white/70 border border-emerald-200 rounded-full px-2 py-0.5 text-[11px] font-mono text-emerald-800">
                            {a}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {!li.configured && li.hasCategory && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-800" data-testid="notify-plant-no-list-warning">
                      <b>No mailing list configured for this category.</b>
                      {isSuperAdmin
                        ? <> Add recipients under <b>Site Settings → Notifications → Plant mailing lists</b>.</>
                        : <> Ask a super admin to add recipients under Site Settings → Notifications.</>}
                    </div>
                  )}
                  {!li.configured && !li.hasCategory && plantEmail && (
                    <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 text-[12px] text-neutral-700">
                      This item has no matching category list — the notification will fall back to the legacy plant email <b>{plantEmail}</b>.
                    </div>
                  )}
                  <div className="flex justify-end gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setConfirmItem(null)}
                      disabled={notifying}
                      data-testid="notify-plant-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={doNotify}
                      disabled={notifying || !canSend}
                      className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
                      data-testid="notify-plant-confirm"
                    >
                      {notifying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
                      Send to Plant
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// ShareLinksModal — Feb 2026 (super admin)
// Mint / list / revoke public gated timeline URLs. Each visitor to
// `/s/{slug}` clears the IP-gate first (same rails as Live Stock /
// Outstanding) and lands on a beautiful auto-refreshing dashboard.
// ============================================================
const ShareLinksModal = ({ onClose, authHeaders, toast }) => {
  const [links, setLinks] = useState(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Feb 2026 — Category-lock composer. `allCategories` is loaded
  // from the public `/api/categories` endpoint; `selectedCats` is
  // the working multi-select for the "Create a new link" composer.
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCats, setSelectedCats] = useState(new Set());

  const load = async () => {
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/export-timeline/share-links`, { headers: authHeaders });
      setLinks(data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(detail);
      setLinks([]);
    }
  };
  const loadCategories = async () => {
    try {
      // Feb 2026 — use the ADMIN categories endpoint (super admin
      // only, returns every category regardless of `is_visible`).
      // The public `/api/categories` filter dropped hidden categories
      // (e.g. `mc-ex`) which the ops team still uses to bake share
      // links for internal viewers.
      const { data } = await axios.get(`${API}/admin/categories`, { headers: authHeaders });
      setAllCategories(Array.isArray(data) ? data : []);
    } catch {
      setAllCategories([]);
    }
  };
  useEffect(() => { load(); loadCategories(); }, []);

  const toggleCat = (slug) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };
  const clearCats = () => setSelectedCats(new Set());

  const mint = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/export-timeline/share-links`,
        {
          label: (label || '').trim(),
          locked_categories: Array.from(selectedCats),
        },
        { headers: authHeaders },
      );
      setLabel('');
      setSelectedCats(new Set());
      await load();
      toast({
        title: 'Share link created',
        description: selectedCats.size > 0
          ? `Locked to ${selectedCats.size} categor${selectedCats.size === 1 ? 'y' : 'ies'}. Anyone with the URL will still need IP approval.`
          : 'Anyone with the URL will still need IP approval.',
      });
    } catch (err) {
      toast({ title: 'Mint failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (slug) => {
    if (!window.confirm(`Revoke ${slug}? Approved visitors will lose access instantly.`)) return;
    try {
      await axios.delete(`${API}/admin/export-timeline/share-links/${slug}`, { headers: authHeaders });
      await load();
      toast({ title: 'Link revoked' });
    } catch (err) {
      toast({ title: 'Revoke failed', description: err?.response?.data?.detail || err?.message });
    }
  };

  // Feb 2026 — inline "Edit lock" state. `editSlug` is the slug of
  // the link currently in edit mode; `editCats` is the draft set of
  // category slugs. Only one row can be open at a time to keep the
  // modal footprint small.
  const [editSlug, setEditSlug] = useState(null);
  const [editCats, setEditCats] = useState(new Set());
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (link) => {
    setEditSlug(link.slug);
    setEditCats(new Set(Array.isArray(link.locked_categories) ? link.locked_categories : []));
  };
  const closeEdit = () => { setEditSlug(null); setEditCats(new Set()); };
  const toggleEditCat = (slug) => {
    setEditCats((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };
  const saveEdit = async () => {
    if (!editSlug) return;
    setEditSaving(true);
    try {
      await axios.patch(
        `${API}/admin/export-timeline/share-links/${editSlug}`,
        { locked_categories: Array.from(editCats) },
        { headers: authHeaders },
      );
      const cnt = editCats.size;
      closeEdit();
      await load();
      toast({
        title: 'Lock updated',
        description: cnt > 0
          ? `Link now locked to ${cnt} categor${cnt === 1 ? 'y' : 'ies'}. Same URL, existing approved viewers keep access.`
          : 'Link is now unlocked (viewer can filter across every category).',
      });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setEditSaving(false);
    }
  };

  const shareUrl = (slug) => `${window.location.origin}/api/s/${slug}`;

  const copy = async (slug) => {
    try {
      await navigator.clipboard.writeText(shareUrl(slug));
      toast({ title: 'Link copied to clipboard' });
    } catch {
      toast({ title: 'Copy failed', description: 'Select the URL manually.' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="export-share-modal">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[#0078D4]" />
            <div>
              <div className="text-sm font-bold text-[#0F172A]">Share Export Timeline</div>
              <div className="text-[10px] text-neutral-500">Super admin only · gated on the same IP allowlist as Outstanding / Live Stock</div>
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} className="rounded-full h-8" data-testid="export-share-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-2 text-xs" data-testid="export-share-error">
              {error}
            </div>
          )}
          {/* Mint composer */}
          <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 mb-2">Create a new link</div>
            <div className="flex gap-2">
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Label (e.g. Sales Team View)"
                className="flex-1 text-sm"
                data-testid="export-share-label"
              />
              <Button
                onClick={mint}
                disabled={busy}
                className="rounded-full h-9 bg-[#0078D4] hover:bg-[#106EBE] text-white text-xs"
                data-testid="export-share-mint"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Share2 className="w-4 h-4 mr-1" />}
                Create Link
              </Button>
            </div>
            {/* Feb 2026 — Optional category-lock. Empty selection =
                unlocked (viewer can filter across all categories).
                Selected slugs are baked into the link so the public
                viewer can only see orders whose items are entirely
                within that scope. */}
            <div className="mt-3" data-testid="export-share-cat-picker">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Lock to categories (optional)
                </div>
                {selectedCats.size > 0 && (
                  <button
                    type="button"
                    onClick={clearCats}
                    className="text-[10px] text-neutral-500 hover:text-neutral-800 underline"
                    data-testid="export-share-cat-clear"
                  >
                    Clear ({selectedCats.size})
                  </button>
                )}
              </div>
              {allCategories.length === 0 ? (
                <div className="text-[11px] text-neutral-500 italic">No categories yet.</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {allCategories.map((c) => {
                    const active = selectedCats.has(c.slug);
                    return (
                      <button
                        key={c.slug}
                        type="button"
                        onClick={() => toggleCat(c.slug)}
                        className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold transition-all ${
                          active
                            ? 'bg-[#0078D4] text-white border-[#0078D4] shadow-sm'
                            : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#0078D4] hover:text-[#0078D4]'
                        }`}
                        data-testid={`export-share-cat-${c.slug}`}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="text-[10px] text-neutral-400 mt-1.5 leading-relaxed">
                Leave empty for an unlocked link — the viewer can then filter across every category live.
                When locked, viewers see only orders where every line belongs to the chosen categories.
              </div>
            </div>
          </div>

          {/* Existing links list */}
          <div className="rounded-xl border border-neutral-200 overflow-hidden">
            <div className="px-3 py-2 bg-neutral-100 text-[10px] font-bold uppercase tracking-wide text-neutral-600">
              Existing links ({links ? links.length : '…'})
            </div>
            {!links && (
              <div className="p-4 text-center text-xs text-neutral-500">
                <Loader2 className="w-4 h-4 mx-auto animate-spin mb-1" /> Loading…
              </div>
            )}
            {links && links.length === 0 && (
              <div className="p-4 text-center text-xs text-neutral-500 italic">
                No share links yet. Create one above.
              </div>
            )}
            {links && links.length > 0 && (
              <div className="divide-y divide-neutral-200 max-h-[320px] overflow-y-auto">
                {links.map((l) => {
                  const revoked = !!l.revoked_at;
                  const lockedCats = Array.isArray(l.locked_categories) ? l.locked_categories : [];
                  const catNameFor = (slug) => {
                    const c = allCategories.find((x) => x.slug === slug);
                    return c?.name || slug;
                  };
                  const isEditing = editSlug === l.slug;
                  return (
                    <div key={l.slug} data-testid={`export-share-row-${l.slug}`}>
                      <div
                        className={`px-3 py-2 flex items-center gap-2 ${revoked ? 'opacity-50' : ''}`}
                      >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-[#0F172A] truncate">{l.label}</span>
                          {revoked && (
                            <span className="text-[9px] font-bold text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5 uppercase">Revoked</span>
                          )}
                          {lockedCats.length > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 uppercase"
                              data-testid={`export-share-row-${l.slug}-lock`}
                              title={`Locked to: ${lockedCats.map(catNameFor).join(', ')}`}
                            >
                              <Lock className="w-2.5 h-2.5" />
                              {lockedCats.length === 1
                                ? catNameFor(lockedCats[0])
                                : `${lockedCats.length} categories`}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-neutral-500 font-mono truncate">{shareUrl(l.slug)}</div>
                        <div className="text-[10px] text-neutral-400 mt-0.5">
                          {l.hits} hit{l.hits === 1 ? '' : 's'} · created {fmtDT(l.created_at)}
                          {l.last_hit_at ? ` · last visit ${fmtDT(l.last_hit_at)}` : ''}
                        </div>
                      </div>
                      {!revoked && (
                        <button
                          type="button"
                          onClick={() => (isEditing ? closeEdit() : openEdit(l))}
                          className={`p-1.5 rounded-full text-neutral-600 ${isEditing ? 'bg-amber-100 text-amber-800' : 'hover:bg-neutral-100'}`}
                          title={isEditing ? 'Close editor' : 'Edit locked categories'}
                          data-testid={`export-share-edit-lock-${l.slug}`}
                        >
                          {isEditing ? <X className="w-3.5 h-3.5" /> : <Edit3 className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => copy(l.slug)}
                        disabled={revoked}
                        className="p-1.5 rounded-full hover:bg-neutral-100 text-neutral-600 disabled:opacity-40"
                        title="Copy link"
                        data-testid={`export-share-copy-${l.slug}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <a
                        href={shareUrl(l.slug)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-1.5 rounded-full hover:bg-neutral-100 text-neutral-600 ${revoked ? 'pointer-events-none opacity-40' : ''}`}
                        title="Open"
                        data-testid={`export-share-open-${l.slug}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      {!revoked && (
                        <button
                          type="button"
                          onClick={() => revoke(l.slug)}
                          className="p-1.5 rounded-full hover:bg-red-50 text-red-600"
                          title="Revoke"
                          data-testid={`export-share-revoke-${l.slug}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      </div>
                      {/* Feb 2026 — inline edit-lock panel. Same URL,
                          same approved viewer list — only the baked
                          category set changes. */}
                      {isEditing && !revoked && (
                        <div
                          className="px-3 pb-3 pt-1 border-t border-amber-100 bg-amber-50/60"
                          data-testid={`export-share-edit-panel-${l.slug}`}
                        >
                          <div className="text-[10px] font-bold uppercase tracking-wide text-amber-800 mb-1.5 flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Edit locked categories
                          </div>
                          {allCategories.length === 0 ? (
                            <div className="text-[11px] text-neutral-500 italic">No categories yet.</div>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {allCategories.map((c) => {
                                const active = editCats.has(c.slug);
                                return (
                                  <button
                                    key={c.slug}
                                    type="button"
                                    onClick={() => toggleEditCat(c.slug)}
                                    className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold transition-all ${
                                      active
                                        ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                                        : 'bg-white text-neutral-700 border-neutral-300 hover:border-amber-500 hover:text-amber-700'
                                    }`}
                                    data-testid={`export-share-edit-cat-${l.slug}-${c.slug}`}
                                  >
                                    {c.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <div className="text-[10px] text-neutral-500 leading-relaxed">
                              {editCats.size === 0
                                ? 'No categories selected — link will be unlocked (all categories visible).'
                                : `${editCats.size} categor${editCats.size === 1 ? 'y' : 'ies'} selected. Same URL. Existing approved viewers keep access.`}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Button
                                onClick={closeEdit}
                                variant="ghost"
                                size="sm"
                                className="rounded-full h-7 text-[11px] px-3"
                                data-testid={`export-share-edit-cancel-${l.slug}`}
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={saveEdit}
                                disabled={editSaving}
                                size="sm"
                                className="rounded-full h-7 text-[11px] px-3 bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid={`export-share-edit-save-${l.slug}`}
                              >
                                {editSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                                Save
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="text-[10px] text-neutral-500 leading-relaxed">
            Approve individual visitors from <b>Admin → Stock Access Control</b>. Only IPs whose <code>approved_slugs</code> include this link&apos;s slug will see the timeline.
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// DailyUpdatesPanel — Feb 2026
// Cross-department activity timeline: every department user can post
// timestamped, long-form updates against the order in the department
// they have permission to edit. All users can view every update from
// every department (read-only). Only the super admin can edit or
// delete an existing update line.
// ============================================================
const DailyUpdatesPanel = ({
  detail, orderId, authHeaders, toast, onChanged,
  deptOptions = {}, displayDeptLabel, visibleDeptKeys,
  displayStatusLabel,
}) => {
  const _dept = displayDeptLabel || ((k) => DEPT_LABEL[k] || k);
  const _stat = displayStatusLabel || ((s) => s);
  const isSuperAdmin = !!detail?.is_super_admin;
  const canEditDept = detail?.can_edit_dept || {};
  const _visibleKeys = visibleDeptKeys || DEPT_KEYS;
  const eligibleDepts = useMemo(
    () => _visibleKeys.filter((d) => !!canEditDept[d]),
    [canEditDept, _visibleKeys.join('|')],
  );
  const currentStages = detail?.department_status || {};

  const [dept, setDept] = useState(eligibleDepts[0] || '');
  const [selectedUpdate, setSelectedUpdate] = useState('');
  const [selectedNote, setSelectedNote] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  // Aug 2026 — Unified composer: the per-dept StageEditor was
  // retired. The Status dropdown + Upload button live inside the
  // Daily Updates panel now, so a single "Post" click can both
  // append a timeline entry and flip the stage status in one write.
  const [status, setStatus] = useState('');
  // Aug 2026 — File is now STAGED locally instead of uploaded on
  // pick. The single Post handler sends the file, status, and
  // dropdown selections as one multipart request so the timeline
  // row + stage docs + status flip all land in one transaction.
  const [stagedFile, setStagedFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const [lightboxAttachment, setLightboxAttachment] = useState(null);

  useEffect(() => {
    if (!dept && eligibleDepts.length) setDept(eligibleDepts[0]);
    if (dept && !eligibleDepts.includes(dept) && eligibleDepts.length) setDept(eligibleDepts[0]);
  }, [eligibleDepts.join('|')]);

  // Reset the pick when the department changes — options are dept-scoped.
  // Sync the Status dropdown to the freshly-selected department's
  // current stage status so a user can flip it up or down.
  useEffect(() => {
    setSelectedUpdate('');
    setSelectedNote('');
    setAdditionalDetails('');
    if (dept) {
      setStatus((currentStages?.[dept]?.status) || 'Pending');
    } else {
      setStatus('');
    }
  }, [dept, currentStages?.[dept]?.status]);

  // Aug 2026 — fallback must be per-field, same reasoning as `_opts`
  // above: a config entry with only `updates` set is truthy, so an
  // object-level fallback never applies and `.notes` crashes the page
  // (ticket 228625, prod white-screen incident).
  const _currentOptsRaw = (dept && deptOptions[dept]) || {};
  const currentOpts = { updates: _currentOptsRaw.updates || [], notes: _currentOptsRaw.notes || [] };
  const canPost = eligibleDepts.length > 0;
  const currentStageStatus = (dept && currentStages?.[dept]?.status) || 'Pending';
  const statusChanged = !!dept && status && status !== currentStageStatus;
  // Aug 2026 — "Pick an Update" is mandatory. Post button stays
  // disabled until a valid Update is selected, regardless of
  // Note / status / attachment state — so the timeline always
  // carries a canonical action label picked from the super-admin
  // configured dropdown, not a note-only or upload-only row.
  const canSubmit = !!selectedUpdate;

  const updates = useMemo(() => {
    const arr = Array.isArray(detail?.daily_updates) ? [...detail.daily_updates] : [];
    // Feb 2026 — Strip only the auto-generated "Uploaded document:
    // <name>" text on attachment-only entries where the text EXACTLY
    // matches that auto-string. Any additional user prose is
    // preserved in full, so user-posted updates render the same way
    // as updates from other departments.
    const cleaned = arr.map((u) => {
      const atts = Array.isArray(u.attachments) ? u.attachments : [];
      if (!atts.length || typeof u.text !== 'string') return u;
      const autoTexts = new Set(atts.map((a) => `Uploaded document: ${a.name || 'Document'}`));
      if (autoTexts.has(u.text.trim())) return { ...u, text: '' };
      return u;
    });
    cleaned.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    return cleaned;
  }, [detail?.daily_updates]);

  // Aug 2026 — Group entries by department under a single header per
  // ticket 228625: the customer wants a note and the document it
  // relates to to read together under one "Accounts" (etc.) block
  // instead of as separate cards. `updates` is already sorted
  // newest-first, so grouping in first-seen order keeps the most
  // recently active department on top, matching prior behavior.
  const groupedUpdates = useMemo(() => {
    const groups = [];
    const byDept = new Map();
    updates.forEach((u) => {
      const key = u.department || '';
      if (!byDept.has(key)) {
        const g = { department: key, items: [] };
        byDept.set(key, g);
        groups.push(g);
      }
      byDept.get(key).items.push(u);
    });
    return groups;
  }, [updates]);

  const submit = async () => {
    if (!canSubmit || !dept) return;
    setPosting(true);
    try {
      // Aug 2026 — Single multipart request bundles: dropdown pick +
      // status change + optional file. All three land as one Mongo
      // write so the composer can never end up in a half-posted
      // state (status flipped but no timeline row, or file uploaded
      // but status not saved, etc.).
      const fd = new FormData();
      fd.append('department', dept);
      fd.append('selected_update', selectedUpdate || '');
      if (selectedNote) fd.append('selected_note', selectedNote);
      if (additionalDetails.trim()) fd.append('additional_details', additionalDetails.trim());
      if (statusChanged) fd.append('status', status);
      if (stagedFile) fd.append('file', stagedFile);
      const { data } = await axios.post(
        `${API}/admin/export-orders/${orderId}/updates`,
        fd,
        { headers: authHeaders },
      );
      const newOrderStatus = data?.new_order_status || null;
      setSelectedUpdate('');
      setSelectedNote('');
      setAdditionalDetails('');
      setStagedFile(null);
      toast({
        title: newOrderStatus === 'shipped'
          ? 'Order shipped'
          : (statusChanged
              ? (stagedFile ? 'Update posted · File uploaded · Status updated' : 'Update posted · Status updated')
              : (stagedFile ? 'Update posted · File uploaded' : 'Update posted')),
        description: newOrderStatus === 'shipped'
          ? `${_dept(dept)} completed — order auto-moved to Shipped.`
          : `${_dept(dept)} · ${fmtDT(new Date())}`,
      });
      onChanged && onChanged();
    } catch (err) {
      toast({ title: 'Post failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setPosting(false);
    }
  };

  // Aug 2026 — File STAGING (was: immediate upload). The Post
  // handler above bundles the staged file into its multipart
  // request so status flip + attachment + timeline row hit the DB
  // in a single transaction.
  const stageFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // allow re-picking the same file
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum 25 MB per attachment.' });
      return;
    }
    setStagedFile(file);
  };
  const clearStagedFile = () => setStagedFile(null);

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3" data-testid="export-daily-updates">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-bold text-[#0F172A] flex items-center gap-1.5">
          <MessageSquarePlus className="w-4 h-4 text-[#0078D4]" /> Daily Updates
          <span className="text-[10px] font-normal text-neutral-500 ml-1">({updates.length})</span>
        </div>
        <div className="text-[10px] text-neutral-500 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Newest first
        </div>
      </div>

      {/* Composer — only visible when the caller has at least one dept
          edit permission (or is super admin). Aug 2026: unified
          composer swallows the retired StageEditor's Status + Upload
          controls so this panel is the single input area for all
          departments. */}
      {canPost && (
        <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-2 mb-3" data-testid="export-daily-updates-composer">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide mb-0.5">Department</div>
              <Select value={dept} onValueChange={setDept}>
                <SelectTrigger data-testid="export-daily-updates-dept">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  {eligibleDepts.map((d) => (
                    <SelectItem key={d} value={d}>{_dept(d)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide mb-0.5 flex items-center justify-between">
                <span>Stage status</span>
                {statusChanged && (
                  <span className="text-[10px] font-bold text-[#0078D4]" data-testid="export-daily-updates-status-changed">
                    Changed
                  </span>
                )}
              </div>
              <Select value={status} onValueChange={setStatus} disabled={!dept}>
                <SelectTrigger data-testid="export-daily-updates-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{_stat(s)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
            <Select
              value={selectedUpdate || undefined}
              onValueChange={(v) => setSelectedUpdate(v)}
              disabled={currentOpts.updates.length === 0}
            >
              <SelectTrigger
                data-testid="export-daily-updates-select-update"
                className={
                  currentOpts.updates.length > 0 && !selectedUpdate
                    ? 'ring-1 ring-amber-400 border-amber-300'
                    : ''
                }
              >
                <SelectValue placeholder={currentOpts.updates.length ? 'Pick an Update… (required)' : 'No updates configured'} />
              </SelectTrigger>
              <SelectContent>
                {currentOpts.updates.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedNote || undefined}
              onValueChange={(v) => setSelectedNote(v)}
              disabled={currentOpts.notes.length === 0}
            >
              <SelectTrigger data-testid="export-daily-updates-select-note">
                <SelectValue placeholder={currentOpts.notes.length ? 'Pick a Note (optional)…' : 'No notes configured'} />
              </SelectTrigger>
              <SelectContent>
                {currentOpts.notes.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <input
            type="text"
            value={additionalDetails}
            onChange={(e) => setAdditionalDetails(e.target.value)}
            placeholder="Additional details (optional, e.g. reference numbers)"
            className="mt-2 w-full rounded-md border border-neutral-200 px-3 py-1.5 text-xs"
            data-testid="export-daily-updates-details"
            maxLength={500}
          />
          {currentOpts.updates.length === 0 && currentOpts.notes.length === 0 && dept && (
            <div className="text-[10px] text-amber-700 mt-1" data-testid="export-daily-updates-empty-hint">
              No dropdown options configured for {_dept(dept)} yet. Ask a super admin to add some under Departments → Dropdown options.
            </div>
          )}
          <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {stagedFile ? (
                <div
                  className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-200 pl-3 pr-1 py-0.5 text-[11px] font-semibold text-sky-800"
                  data-testid="export-daily-updates-staged-file"
                  title={stagedFile.name}
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span className="max-w-[220px] truncate">{stagedFile.name}</span>
                  <span className="opacity-60 tabular-nums">
                    {stagedFile.size >= 1024 * 1024
                      ? `${(stagedFile.size / (1024 * 1024)).toFixed(1)} MB`
                      : `${Math.max(1, Math.round(stagedFile.size / 1024))} KB`}
                  </span>
                  <button
                    type="button"
                    onClick={clearStagedFile}
                    className="w-5 h-5 rounded-full bg-sky-100 hover:bg-sky-200 text-sky-800 flex items-center justify-center text-[13px] leading-none"
                    aria-label="Remove staged file"
                    data-testid="export-daily-updates-staged-file-clear"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className={`inline-flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-1 text-xs cursor-pointer hover:bg-neutral-50 ${!dept ? 'opacity-40 pointer-events-none' : ''}`}>
                  <Upload className="w-3.5 h-3.5" />
                  Attach document
                  <input
                    type="file"
                    className="hidden"
                    onChange={stageFile}
                    disabled={!dept}
                    data-testid="export-daily-updates-upload"
                  />
                </label>
              )}
            </div>
            <Button
              onClick={submit}
              disabled={posting || !canSubmit}
              size="sm"
              className="rounded-full h-8 text-xs bg-[#0078D4] hover:bg-[#106EBE] text-white"
              data-testid="export-daily-updates-submit"
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5 mr-1" />}
              Post update
            </Button>
          </div>
        </div>
      )}
      {!canPost && (
        <div className="text-[11px] text-neutral-500 italic mb-3" data-testid="export-daily-updates-readonly-hint">
          You have read-only access — assign yourself a department permission to post updates.
        </div>
      )}

      {/* Timeline list */}
      {updates.length === 0 ? (
        <div className="text-xs text-neutral-500 italic py-3 text-center" data-testid="export-daily-updates-empty">
          No updates yet. Be the first to log activity on this order.
        </div>
      ) : (
        <div className="space-y-3">
          {groupedUpdates.map((group) => (
            <div key={group.department} className="rounded-lg border border-neutral-200 bg-neutral-50/60 p-2">
              <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
                <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold border ${STATUS_TONE.Submitted}`}>
                  {DEPT_LABEL[group.department] || group.department}
                </span>
              </div>
              <div className="space-y-2">
                {group.items.map((u) => (
                  <UpdateRow
                    key={u.id}
                    row={u}
                    orderId={orderId}
                    isSuperAdmin={isSuperAdmin}
                    authHeaders={authHeaders}
                    toast={toast}
                    onChanged={onChanged}
                    onOpenAttachment={setLightboxAttachment}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <AttachmentLightbox
        open={!!lightboxAttachment}
        attachment={lightboxAttachment}
        onClose={() => setLightboxAttachment(null)}
      />
    </div>
  );
};

const UpdateRow = ({ row, orderId, isSuperAdmin, authHeaders, toast, onChanged, onOpenAttachment }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.text || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraft(row.text || ''); }, [row.text]);

  const formatTs = (iso) => fmtDT(iso);

  const saveEdit = async () => {
    const body = (draft || '').trim();
    if (!body) {
      toast({ title: 'Update text is required' });
      return;
    }
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/export-orders/${orderId}/updates/${row.id}`,
        { text: body },
        { headers: authHeaders },
      );
      toast({ title: 'Update edited' });
      setEditing(false);
      onChanged && onChanged();
    } catch (err) {
      toast({ title: 'Edit failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this update? This cannot be undone.')) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/admin/export-orders/${orderId}/updates/${row.id}`,
        { headers: authHeaders },
      );
      toast({ title: 'Update deleted' });
      // Aug 2026 — Await the parent's re-fetch so the caller has
      // pulled fresh detail before we drop `busy`. Previously we
      // fired `onChanged()` and returned immediately, so the row
      // could briefly re-render (stale detail) before the refresh
      // landed. `onChanged` may be sync-void or return a promise;
      // Promise.resolve() normalises both cases.
      await Promise.resolve(onChanged && onChanged());
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-neutral-200 bg-white p-2.5"
      data-testid={`export-daily-update-${row.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold border ${STATUS_TONE.Submitted}`}>
            {DEPT_LABEL[row.department] || row.department}
          </span>
          <span className="text-neutral-500">{row.author_email}</span>
        </div>
        <div className="text-[10px] text-neutral-500 tabular-nums flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatTs(row.created_at)}
          {row.edited_at && (
            <span
              className="ml-1 italic text-amber-700"
              title={`Edited by ${row.edited_by || 'super admin'} at ${formatTs(row.edited_at)}`}
              data-testid={`export-daily-update-edited-${row.id}`}
            >
              · edited
            </span>
          )}
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="text-xs"
            data-testid={`export-daily-update-edit-textarea-${row.id}`}
          />
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setEditing(false); setDraft(row.text || ''); }}
              className="h-7 text-[11px]"
              data-testid={`export-daily-update-edit-cancel-${row.id}`}
            >
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={busy}
              size="sm"
              className="h-7 text-[11px] bg-amber-700 hover:bg-amber-800 text-white rounded-full"
              data-testid={`export-daily-update-edit-save-${row.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      ) : (
        row.text ? (
          <div className="text-xs text-[#0F172A] whitespace-pre-wrap break-words" data-testid={`export-daily-update-text-${row.id}`}>
            {row.text}
          </div>
        ) : null
      )}
      {/* Aug 2026 — Attachments render as a compact "docs" text
          link. Click opens the in-page lightbox (same as before)
          instead of navigating out to Drive. */}
      {Array.isArray(row.attachments) && row.attachments.length > 0 && (
        <div
          className="mt-1 inline-flex flex-wrap gap-1.5"
          data-testid={`export-daily-update-attachments-${row.id}`}
        >
          {row.attachments.map((att) => {
            if (!att.file_id) return null;
            return (
              <button
                key={att.file_id}
                type="button"
                onClick={() => onOpenAttachment && onOpenAttachment(att)}
                className="inline-block px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 hover:border-blue-300 hover:underline transition"
                title={att.name || 'Document'}
                data-testid={`export-daily-update-attachment-${row.id}-${att.file_id}`}
              >
                docs
              </button>
            );
          })}
        </div>
      )}
      {isSuperAdmin && !editing && (
        <div className="flex justify-end gap-1 mt-1.5 pt-1.5 border-t border-neutral-100" data-testid={`export-daily-update-actions-${row.id}`}>
          {!row._legacy && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 text-[10px] text-amber-700 hover:text-amber-900"
              data-testid={`export-daily-update-edit-${row.id}`}
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )}
          {/* Aug 2026 — Delete is available for EVERY row a super
              admin sees, including synthetic `_legacy` document
              rows minted at read-time. The backend now recognises
              the `legacy-doc-<file_id>` id and strips the source
              document from `department_status[dept].documents`. */}
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            className="inline-flex items-center gap-1 text-[10px] text-red-600 hover:text-red-800 ml-2"
            data-testid={`export-daily-update-delete-${row.id}`}
          >
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminExportDashboard;
